import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { bebauungsplaeneApi } from '../api/bebauungsplaene';
import BPlanGeometryEditor from '../components/BPlanGeometryEditor';
import DocumentList from '../components/DocumentList';
import { useDocumentConfig } from '../hooks/useConfigVisibility';
import { useAuth } from '../context/AuthContext';
import {
  GEBIETSARTEN,
  GEBIETSART_LABELS,
  type BPlanGeometry,
  type Gebietsart,
} from '../types/bebauungsplan';

interface FormState {
  name: string;
  plan_nr: string;
  gebietsart: Gebietsart;
  rechtskraft_datum: string;
  notizen: string;
  geometry: BPlanGeometry | null;
}

export default function BebauungsplanDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Backwards-compat: /bebauungsplaene/neu erzeugt einen Draft im Backend und
  // navigiert auf die echte ID. Die Listenseite trifft diesen Pfad nicht mehr,
  // aber alte Lesezeichen / der Browser-Back-Button funktionieren weiterhin.
  const isNeuRoute = id === 'neu';
  const bplanId = !isNeuRoute && id ? Number(id) : null;

  const draftRedirectRef = useRef(false);
  useEffect(() => {
    if (!isNeuRoute || draftRedirectRef.current) return;
    draftRedirectRef.current = true;
    bebauungsplaeneApi.create().then((bp) => {
      queryClient.invalidateQueries({ queryKey: ['bebauungsplaene'] });
      navigate(`/bebauungsplaene/${bp.id}`, { replace: true });
    });
  }, [isNeuRoute, navigate, queryClient]);

  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string>('');
  const docTab = useDocumentConfig('bebauungsplan').tab;

  const { data: bplan, isLoading } = useQuery({
    queryKey: ['bebauungsplan', bplanId],
    queryFn: () => bebauungsplaeneApi.get(bplanId as number),
    enabled: bplanId !== null,
  });

  useEffect(() => {
    if (bplan) {
      setForm({
        name: bplan.name,
        plan_nr: bplan.plan_nr ?? '',
        gebietsart: bplan.gebietsart,
        rechtskraft_datum: bplan.rechtskraft_datum ?? '',
        notizen: bplan.notizen ?? '',
        geometry: bplan.geometry_geojson,
      });
    }
  }, [bplan]);

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!bplanId || !form) throw new Error('Keine ID');
      return bebauungsplaeneApi.update(bplanId, {
        name: form.name.trim() || 'Unbenannt',
        plan_nr: form.plan_nr.trim() || null,
        gebietsart: form.gebietsart,
        rechtskraft_datum: form.rechtskraft_datum || null,
        notizen: form.notizen.trim() || null,
        geometry_geojson: form.geometry,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bebauungsplaene'] });
      queryClient.invalidateQueries({ queryKey: ['bebauungsplan', bplanId] });
      setError('');
    },
    onError: (e: Error) => setError(e.message || 'Fehler beim Speichern.'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => bebauungsplaeneApi.remove(bplanId as number),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bebauungsplaene'] });
      navigate('/bebauungsplaene');
    },
  });

  const handleSubmit = () => {
    setError('');
    if (!form) return;
    updateMutation.mutate();
  };

  const cityCenter: [number, number] | undefined = user?.city?.default_map_center
    ? [user.city.default_map_center.latitude, user.city.default_map_center.longitude]
    : undefined;

  // Initialer Karten-Mittelpunkt: bbox des B-Plans > Stadt-Default > Deutschland-Mitte.
  const initialCenter: [number, number] | undefined =
    bplan && bplan.bbox_min_lat != null && bplan.bbox_max_lat != null &&
    bplan.bbox_min_lon != null && bplan.bbox_max_lon != null
      ? [
          (bplan.bbox_min_lat + bplan.bbox_max_lat) / 2,
          (bplan.bbox_min_lon + bplan.bbox_max_lon) / 2,
        ]
      : cityCenter;

  const inputClass =
    'w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

  if (isNeuRoute) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-400 text-sm">Neuer Bebauungsplan wird angelegt…</div>
      </div>
    );
  }

  if (isLoading || !form) {
    return (
      <div className="min-h-screen bg-gray-50">
        <main className="max-w-7xl mx-auto px-4 py-6 text-gray-400">Lade…</main>
      </div>
    );
  }

  const hasGeometry = form.geometry != null;

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <button
              onClick={() => navigate('/bebauungsplaene')}
              className="text-sm text-gray-500 hover:text-gray-700 mb-2"
            >
              ← Bebauungspläne
            </button>
            <h1 className="text-2xl font-semibold text-gray-900">
              {form.name || 'Bebauungsplan'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (window.confirm('Diesen Bebauungsplan wirklich ausblenden?')) {
                  deleteMutation.mutate();
                }
              }}
              className="text-sm text-red-600 hover:text-red-700 px-3 py-2"
            >
              Ausblenden
            </button>
            <button
              onClick={handleSubmit}
              disabled={updateMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg"
            >
              Speichern
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-4">
            {error}
          </div>
        )}
        {updateMutation.isSuccess && !error && (
          <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 mb-4">
            Gespeichert.
          </div>
        )}
        {!hasGeometry && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg px-4 py-3 mb-4">
            Polygon noch nicht gezeichnet — bitte auf der Karte zeichnen und speichern. Solange
            kein Polygon existiert, wird dieser B-Plan beim Inserat-Abgleich übersprungen.
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow p-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => f && { ...f, name: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Plan-Nr.</label>
                  <input
                    type="text"
                    value={form.plan_nr}
                    onChange={(e) => setForm((f) => f && { ...f, plan_nr: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Rechtskraftdatum</label>
                  <input
                    type="date"
                    value={form.rechtskraft_datum}
                    onChange={(e) =>
                      setForm((f) => f && { ...f, rechtskraft_datum: e.target.value })
                    }
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Gebietsart *</label>
                <select
                  value={form.gebietsart}
                  onChange={(e) =>
                    setForm((f) => f && { ...f, gebietsart: e.target.value as Gebietsart })
                  }
                  className={inputClass}
                >
                  {GEBIETSARTEN.map((code) => (
                    <option key={code} value={code}>
                      {GEBIETSART_LABELS[code]}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  „Reines Wohngebiet (WR)" wird auf der Inserat-Seite rot hervorgehoben (Ferienwohnungen i. d. R. unzulässig).
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Notizen</label>
                <textarea
                  value={form.notizen}
                  onChange={(e) => setForm((f) => f && { ...f, notizen: e.target.value })}
                  rows={4}
                  className={inputClass}
                />
              </div>
            </div>

            {docTab && (
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Dokumente</h3>
                <DocumentList entityType="bebauungsplan" entityId={bplanId as number} />
              </div>
            )}
          </div>

          <BPlanGeometryEditor
            value={form.geometry}
            onChange={(g) => setForm((f) => f && { ...f, geometry: g })}
            initialCenter={initialCenter}
          />
        </div>
      </main>
    </div>
  );
}
